/**
 * @babel/polyfill@^7.4.0 is supposed to include `Array.prototype.flat`, but that doesn't work of us -
 * presumably because sub-dependencies still include babel 6 and the corresponding babel-polyfill package.
 * So we include this polyfill for now.
 *
 * https://github.com/babel/babel/issues/9749
 */
import 'array-flat-polyfill'
import { List } from 'immutable'
import { Outlet, isRouteErrorResponse, useLocation, useMatches, useRouteError } from 'react-router'
import type { UIMatch } from 'react-router'
import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import cc from 'classcat'
import dndPageScroll from 'dnd-page-scroll'

import type { SidebarModuleName } from '../components/EditorSidebar'
import {
  activateEditorSidebarModule,
  getDefaultThemeSettings,
  getResellerName,
  getTax,
  getThemeMeta,
  getThemeSettingsSchema,
  getThemes,
  loadShopEmail,
} from '../store/actions'
import { getPlain } from '../store/utils'
import BottomBar from '../components/bottomBar/BottomBar'
import EditorSidebar from '../components/EditorSidebar'
import ErrorPage from '../components/ErrorPage'
import NotificationOverlay from '../components/NotificationOverlay'
import ThemeStylesForEditorPreview from '../components/ThemeStylesForEditorPreview'
import preventFileDrop from '../utils/preventFileDrop'

export default function Editor() {
  const isBeyond = useSelector<State, boolean>((state) => Boolean(state.getIn(['shop', 'beyond'])))
  const hasThemeLoaded = useSelector<State, boolean>((state) => state.getIn(['view', 'hasThemeLoaded'], false))
  const mboMenuMode = useSelector<State, boolean>((state) => state.getIn(['view', 'mboMenuMode']))
  const locales = useSelector<State, ImmutableList>((state) => state.getIn(['shop', 'locales']))
  const activeEditorSidebarModule = useSelector<State, SidebarModuleName>((state) =>
    state.getIn(['view', 'editorSidebar', 'activeModule']),
  )
  const navigation = useSelector<State, ImmutableMap>((state) => state.get('navigation'))
  const legalPages = useSelector<State, ImmutableList>((state) => state.get('legalPages', List()))
  const footerPages = useSelector<State, ImmutableList>((state) => state.get('footerPages'))

  const location = useLocation()

  useEffect(() => {
    document.body.classList.add('ep-is-editor-mode')
    dndPageScroll({
      node: document.querySelector('.editor-body-wrapper'),
      namespace: '-workspace',
    })
    dndPageScroll({
      node: document.querySelector('.ep-sidebar-module-page-manager .ep-scrollable'),
      namespace: '-page-manager',
      listenGlobally: false,
    })
  }, [])

  let viewError = useSelector<State, any>((state) => getPlain(state.getIn(['view', 'error'])))
  viewError = getPlain(viewError)
  const error = useRouteError()
  if (isRouteErrorResponse(error)) {
    viewError = { ...error, message: error.data?.message ?? '' }
  }

  // kind of hacky solution to grab into the child routes
  const matches = useMatches() as UIMatch<unknown, { Component: { contentCreationDisabled?: () => boolean } }>[]
  const childComponent = matches?.at(-1)?.handle?.Component
  const contentCreationDisabled =
    Boolean(viewError) ||
    Boolean(childComponent && childComponent.contentCreationDisabled && childComponent.contentCreationDisabled())
  const editorDisabled = Boolean(viewError?.statusCode === 429)

  const content = editorDisabled ? (
    <ErrorPage statusCode={viewError.get('statusCode')} requestId={viewError.get('requestId')} />
  ) : (
    <>
      {hasThemeLoaded && (
        <>
          <BottomBar showLanguages={!isBeyond && locales.count() > 1} />
          <ThemeStylesForEditorPreview />
        </>
      )}
      <div className="editor-body-wrapper" id="body-wrapper">
        <Outlet />
      </div>
    </>
  )

  return (
    <div className="ep-is-editor-mode" onDragOverCapture={preventFileDrop}>
      <meta name="robots" content="noindex" />
      <EditorSidebar
        location={location}
        legalPages={legalPages}
        footerPages={footerPages}
        navigation={navigation ? navigation.get('editor') : List()}
        mboMenuMode={mboMenuMode}
        activeModule={activeEditorSidebarModule}
        contentCreationDisabled={contentCreationDisabled}
        editorDisabled={editorDisabled}
      />
      <div className={cc(['ep-editor-main', hasThemeLoaded && 'ep-theme-dom'])}>{content}</div>
      {!editorDisabled && <NotificationOverlay />}
    </div>
  )
}

Editor.storeUpdate = ({ location: { pathname } }: RouteComponentProps, state: State) => {
  return [
    !state.get('themeSettingsSchema') ? getThemeSettingsSchema(state.getIn(['shop', 'themeId'])) : null,
    !state.get('defaultThemeSettings') ? getDefaultThemeSettings(state.getIn(['shop', 'themeId'])) : null,
    !state.get('themeMeta') ? getThemeMeta(state.getIn(['shop', 'themeId'])) : null,
    state.getIn(['shop', 'beyond']) && !state.get('resellerName') ? getResellerName() : null,
    state.getIn(['shop', 'beyond']) && !state.get('tax') ? getTax() : null,
    !state.getIn(['shop', 'email']) ? loadShopEmail() : null,
    !state.get('themes') ? getThemes() : null,
    pathname?.endsWith('/themes') && state.getIn(['view', 'editorSidebar', 'activeModule']) !== 'theme-manager'
      ? activateEditorSidebarModule('theme-manager')
      : null,
  ]
}

Editor.loadableSsrChunks = (_props: RouteComponentProps, state: State) => {
  const chunks = ['editor']
  if (state.getIn(['shop', 'beyond'])) chunks.push('account')
  return chunks
}
